import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

/** Не передан `input.telegram`. */
export const INTEGRATION_INPUT_MISSING_CODE = 24400;
/** Интеграция не найдена. */
export const INTEGRATION_NOT_FOUND_CODE = 24404;
/** У организации уже есть интеграция — она одна на организацию. */
export const INTEGRATION_ALREADY_EXISTS_CODE = 24409;
/** Канал не найден / не канал / бот не админ с правом публикации / invite-ссылка / Telegram недоступен. */
export const INTEGRATION_INVALID_CHANNEL_CODE = 24422;

/** Статус ошибки, когда картинку поста не удалось скачать из хранилища до отправки. */
export const IMAGE_DOWNLOAD_ERROR_STATUS = 'IMAGE_DOWNLOAD';

/** Ограничения `POST /tournament-integrations/{id}/posts`. */
export const POST_TEXT_MAX_LENGTH = 4096;
export const POST_IMAGES_MAX_COUNT = 10;

/**
 * Сообщения бэкенда английские и технические («Invalid telegram channel "@x"»), поэтому
 * пользователю показываем свой текст по коду. GraphQL-ошибки приходят кодом в
 * `extensions.code`, REST — HTTP-статусом.
 */
const MESSAGES_BY_CODE: Record<number, MessageDescriptor> = {
  [INTEGRATION_INPUT_MISSING_CODE]: msg`Укажите канал Telegram.`,
  [INTEGRATION_NOT_FOUND_CODE]: msg`Подключение не найдено — возможно, его уже удалили. Обновите страницу.`,
  [INTEGRATION_ALREADY_EXISTS_CODE]: msg`К организации уже подключён канал. Удалите его, чтобы подключить другой.`,
  [INTEGRATION_INVALID_CHANNEL_CODE]: msg`Не удалось подключить канал. Проверьте, что канал существует, бот добавлен в его администраторы с правом публикации сообщений, а адрес взят по инструкции выше: t.me/имя для публичного канала или адрес из web.telegram.org для закрытого.`,
  400: msg`Пост не прошёл проверку: нужен текст до 4096 символов или картинка (до 10 файлов jpeg, png или webp).`,
  403: msg`Недостаточно прав: интеграциями управляет администратор организации.`,
  404: msg`Подключение не найдено — возможно, его уже удалили. Обновите страницу.`,
  409: msg`Бот потерял доступ к каналу. Добавьте его в администраторы канала снова или переподключите канал в настройках.`,
  413: msg`Пост больше 5 МБ — Telegram его не примет.`,
  502: msg`Telegram отклонил пост или сейчас недоступен. Попробуйте позже.`,
};

const IMAGE_DOWNLOAD_MESSAGE = msg`Не удалось загрузить картинку для поста. Попробуйте ещё раз.`;

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

function readCode(value: unknown): number | undefined {
  const code = Number(value);
  return Number.isFinite(code) && code > 0 ? code : undefined;
}

/** Код ошибки из ответа RTK Query: `extensions.code` GraphQL-ошибки или HTTP-статус REST. */
export function getIntegrationErrorCode(error: unknown): number | string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const { status, data } = error as ErrorLike;

  if (status === IMAGE_DOWNLOAD_ERROR_STATUS) return IMAGE_DOWNLOAD_ERROR_STATUS;

  // graphqlBaseQuery кладёт в `data` массив `errors` (`shared/api/base-api.ts`).
  if (Array.isArray(data)) {
    const code = readCode((data[0] as { extensions?: { code?: unknown } } | undefined)?.extensions?.code);
    if (code !== undefined) return code;
  }

  return readCode(status);
}

/** Понятное пользователю сообщение об ошибке интеграции; `fallback` — для неизвестного кода. */
export function getIntegrationErrorMessage(error: unknown, fallback: MessageDescriptor): MessageDescriptor {
  const code = getIntegrationErrorCode(error);

  if (code === IMAGE_DOWNLOAD_ERROR_STATUS) return IMAGE_DOWNLOAD_MESSAGE;
  if (typeof code === 'number' && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];

  return fallback;
}
